export function getSeoOnPage(page = 'DEFAULT', config = {}) {
    const {title, description, image, url, keywords, facebook} = getSeo(page, config);
    let seo = {
        title,
        description,
        canonical: url,
        openGraph: {
            type: 'website',
            locale: 'vi_VN',
            url,
            title,
            description,
            siteName: 'TNH Hospital Group',
        },
        additionalMetaTags: [
            {
                name: 'keywords',
                content: keywords,
            },
        ],
    };
    if (image) {
        seo.openGraph.images = [
            {
                url: image,
                alt: title,
            },
        ];
    }
    if (facebook) {
        seo.facebook = {
            appId: facebook,
        };
    }
    return seo;
}

const getSeo = (page = 'DEFAULT', data = {}) => {
    // Lấy thông tin SEO từ config API
    // Keys từ backend:
    // - WEB_TITLE: Tiêu đề website
    // - WEB_KEYWORDS: Từ khóa SEO
    // - WEB_SHORT_DESCRIPTION: Mô tả ngắn
    // - WEB_LINK_SEO: Canonical URL (ưu tiên)
    // - WEB_IMAGE_SEO: Hình ảnh SEO/OG
    // - WEB_FB_APPID: Facebook App ID
    let seo = {
        title: data['WEB_TITLE']?.value || 'CTCP Tập đoàn Bệnh viện TNH',
        description:
            data['WEB_SHORT_DESCRIPTION']?.value ||
            'CTCP Tập đoàn Bệnh viện TNH - Chuyên cung cấp dịch vụ y tế uy tín, chất lượng.',
        keywords: data['WEB_KEYWORDS']?.value || 'TNH, Bệnh viện, Y tế',
        image: data['WEB_IMAGE_SEO']?.value || null,
        url:
            data['WEB_LINK_SEO']?.value ||
            data['WEB_URL_SEO']?.value ||
            process.env.NEXT_PUBLIC_WEB_URL ||
            'https://tnh.com.vn',
        facebook: data['WEB_FB_APPID']?.value || null,
    };

    // Hỗ trợ SEO cho từng page cụ thể (nếu có config riêng)
    (Object.keys(data) || []).forEach(key => {
        if (key.includes(`_${page}_`)) {
            if (key.includes('_TITLE') && data[key]?.value) {
                seo.title = data[key].value;
            }
            if (key.includes('_DESCRIPTION') && data[key]?.value) {
                seo.description = data[key].value;
            }
            if (key.includes('_IMAGE') && data[key]?.value) {
                seo.image = data[key].value;
            }
            if (key.includes('_URL') && data[key]?.value) {
                seo.url = data[key].value;
            }
            if (key.includes('_KEYWORDS') && data[key]?.value) {
                seo.keywords = data[key].value;
            }
            if (key.includes('_FACEBOOK') && data[key]?.value) {
                seo.facebook = data[key].value;
            }
        }
    });

    return seo;
};

export const getCustomSEO = (title, description, url, keywords, urlImage) => {
    const webUrl = process.env.NEXT_PUBLIC_WEB_URL || 'https://tnh.com.vn';

    // Xử lý image URL - nếu có URL hợp lệ thì dùng, không thì dùng logo mặc định
    let imageUrl = '';
    if (urlImage && urlImage.trim() !== '') {
        // Nếu là URL đầy đủ (bắt đầu với http/https), dùng trực tiếp
        // Nếu là relative path, thêm domain
        imageUrl =
            urlImage.startsWith('http://') || urlImage.startsWith('https://')
                ? urlImage
                : `${webUrl}${urlImage.startsWith('/') ? '' : '/'}${urlImage}`;
    } else {
        // Fallback to default logo
        imageUrl = `${webUrl}/bw_image/logo.jpg`;
    }

    return {
        title,
        description,
        canonical: url,
        additionalMetaTags: [
            {
                name: 'keywords',
                content: keywords,
            },
        ],
        openGraph: {
            type: 'website',
            locale: 'vi_VN',
            url,
            title,
            description,
            siteName: 'TNH Hospital Group',
            images: [
                {
                    url: imageUrl,
                    width: 800,
                    height: 600,
                    alt: title,
                },
            ],
        },
        twitter: {
            cardType: 'summary_large_image',
        },
    };
};

/**
 * Tạo SEO tự động từ data thực tế của trang
 * Ưu tiên: Config từ API > Data tự động > Default
 */
export function getAutoSEO(pageName, config = {}, pageData = {}) {
    // Lấy SEO từ config API (ưu tiên cao nhất)
    let seoFromConfig = getSeoOnPage(pageName, config);

    // Tạo SEO từ data tự động nếu config không có
    let autoSeo = {
        title: seoFromConfig.title,
        description: seoFromConfig.description,
        keywords: seoFromConfig.additionalMetaTags?.[0]?.content || '',
    };

    // Generate SEO từ data theo từng loại trang
    switch (pageName) {
        case 'TINTUC':
            if (pageData.totalItems && !config[`WEB_TINTUC_TITLE`]) {
                autoSeo.title = `Tin tức y tế - ${pageData.totalItems} bài viết | TNH Hospital`;
                autoSeo.description = `Khám phá ${pageData.totalItems}+ bài viết tin tức y tế, sức khỏe, cập nhật mới nhất từ CTCP Tập đoàn Bệnh viện TNH.`;
                autoSeo.keywords = `tin tức y tế, sức khỏe, bệnh viện TNH${pageData.categoryName ? `, ${pageData.categoryName}` : ''}`;
            }
            break;

        case 'BACSI':
            if (!config[`WEB_BACSI_TITLE`]) {
                autoSeo.title = `Chuyên gia bác sĩ | CTCP Tập đoàn Bệnh viện TNH`;
                autoSeo.description = `Danh sách bác sĩ, chuyên gia hàng đầu tại CTCP Tập đoàn Bệnh viện TNH. Đặt lịch khám ngay.`;
                autoSeo.keywords = `bác sĩ TNH, chuyên gia y tế, bác sĩ giỏi${pageData.departmentName ? `, ${pageData.departmentName}` : ''}`;
            }
            break;

        case 'SPECIALTY':
            if (pageData.totalSpecialties && !config[`WEB_SPECIALTY_TITLE`]) {
                autoSeo.title = `Chuyên khoa - ${pageData.totalSpecialties} chuyên khoa | TNH Hospital`;
                autoSeo.description = `Khám phá ${pageData.totalSpecialties}+ chuyên khoa y tế tại TNH. Điều trị đa chuyên khoa với đội ngũ bác sĩ giàu kinh nghiệm.`;
                autoSeo.keywords = `chuyên khoa y tế, khám đa chuyên khoa, bệnh viện TNH`;
            }
            break;

        case 'UUDAI':
            if (pageData.totalPromotions && !config[`WEB_UUDAI_TITLE`]) {
                autoSeo.title = `Ưu đãi khám chữa bệnh - ${pageData.totalPromotions} chương trình | TNH Hospital`;
                autoSeo.description = `Cập nhật ${pageData.totalPromotions}+ chương trình ưu đãi, khuyến mãi khám chữa bệnh tại TNH. Giảm giá lên đến 50%.`;
                autoSeo.keywords = `ưu đãi y tế, khuyến mãi khám bệnh, giảm giá TNH`;
            }
            break;

        case 'MEDICAL-FACILITY':
            if (!config[`WEB_MEDICAL-FACILITY_TITLE`]) {
                autoSeo.title = `Hệ thống bệnh viện | CTCP Tập đoàn Bệnh viện TNH`;
                autoSeo.description = `Hệ thống bệnh viện và cơ sở y tế TNH trên toàn quốc. Tìm bệnh viện gần bạn nhất.`;
                autoSeo.keywords = `bệnh viện TNH, cơ sở y tế, hệ thống bệnh viện`;
            }
            break;

        case 'GOIDICHVU':
            if (pageData.totalPackages && !config[`WEB_GOIDICHVU_TITLE`]) {
                autoSeo.title = `Gói dịch vụ y tế - ${pageData.totalPackages} gói | TNH Hospital`;
                autoSeo.description = `Khám phá ${pageData.totalPackages}+ gói dịch vụ y tế toàn diện tại TNH. Chăm sóc sức khỏe một cách tiết kiệm và hiệu quả.`;
                autoSeo.keywords = `gói dịch vụ y tế, khám sức khỏe tổng quát, gói khám TNH`;
            }
            break;

        case 'BAOCHI':
            if (pageData.totalArticles && !config[`WEB_BAOCHI_TITLE`]) {
                autoSeo.title = `Báo chí nói về TNH - ${pageData.totalArticles} bài viết | TNH Hospital`;
                autoSeo.description = `Tổng hợp ${pageData.totalArticles}+ bài viết, tin tức báo chí về CTCP Tập đoàn Bệnh viện TNH.`;
                autoSeo.keywords = `báo chí TNH, tin tức bệnh viện, TNH trên báo`;
            }
            break;

        case 'JOB':
            if (pageData.totalJobs && !config[`WEB_JOB_TITLE`]) {
                autoSeo.title = `Tuyển dụng - ${pageData.totalJobs} vị trí | TNH Hospital`;
                autoSeo.description = `Cơ hội nghề nghiệp tại TNH với ${pageData.totalJobs}+ vị trí tuyển dụng. Môi trường làm việc chuyên nghiệp, đãi ngộ tốt.`;
                autoSeo.keywords = `tuyển dụng TNH, việc làm y tế, nghề nghiệp bệnh viện`;
            }
            break;

        case 'SERVICE_PRODUCT':
            if (pageData.totalProducts && !config[`WEB_SERVICE_PRODUCT_TITLE`]) {
                autoSeo.title = `Sản phẩm dịch vụ - ${pageData.totalProducts} sản phẩm | TNH Hospital`;
                autoSeo.description = `Khám phá ${pageData.totalProducts}+ sản phẩm dịch vụ y tế tại TNH. Chăm sóc sức khỏe toàn diện, chất lượng cao.`;
                autoSeo.keywords = `sản phẩm y tế, dịch vụ y tế, sản phẩm TNH`;
            }
            break;

        case 'SEARCH':
            // SEO động từ keyword search
            if (pageData.searchKeyword && !config[`WEB_SEARCH_TITLE`]) {
                const keyword = pageData.searchKeyword;
                const filterType = pageData.filterType; // 0=all, 1=doctor, 2=service, 3=department
                let filterLabel = 'tất cả';
                if (filterType === 1) filterLabel = 'bác sĩ';
                else if (filterType === 2) filterLabel = 'dịch vụ';
                else if (filterType === 3) filterLabel = 'chuyên khoa';

                autoSeo.title = `Tìm kiếm "${keyword}" trong ${filterLabel} | TNH Hospital`;
                autoSeo.description = `Kết quả tìm kiếm "${keyword}" trong ${filterLabel} tại CTCP Tập đoàn Bệnh viện TNH.`;
                autoSeo.keywords = `${keyword}, tìm kiếm ${filterLabel}, TNH Hospital`;
            } else if (!config[`WEB_SEARCH_TITLE`]) {
                autoSeo.title = `Tìm kiếm | TNH Hospital`;
                autoSeo.description = `Tìm kiếm bác sĩ, dịch vụ, chuyên khoa tại CTCP Tập đoàn Bệnh viện TNH.`;
                autoSeo.keywords = `tìm kiếm y tế, tìm bác sĩ, tìm dịch vụ TNH`;
            }
            break;

        case 'HOME':
            // SEO từ stats
            if (pageData.facilities && pageData.doctors && !config[`WEB_HOME_TITLE`]) {
                autoSeo.title = `CTCP Tập đoàn Bệnh viện TNH - ${pageData.facilities} cơ sở, ${pageData.doctors}+ bác sĩ | TNH Hospital`;
                autoSeo.description = `Hệ thống ${pageData.facilities} cơ sở y tế với ${pageData.doctors}+ bác sĩ chuyên khoa, ${pageData.beds}+ giường bệnh. Phục vụ ${pageData.patients}+ bệnh nhân.`;
                autoSeo.keywords = `bệnh viện TNH, ${pageData.facilities} cơ sở, ${pageData.doctors} bác sĩ, y tế chất lượng cao`;
            }
            break;
    }

    // Merge: Config có ưu tiên cao hơn auto-generated
    // Xử lý page name có dấu gạch ngang (VD: MEDICAL-FACILITY -> MEDICALFACILITY)
    const pageKey = pageName.replace(/-/g, '');
    const finalSeo = {
        ...seoFromConfig,
        title:
            config[`WEB_${pageKey}_TITLE`]?.value ||
            config[`WEB_${pageName}_TITLE`]?.value ||
            autoSeo.title ||
            seoFromConfig.title,
        description:
            config[`WEB_${pageKey}_DESCRIPTION`]?.value ||
            config[`WEB_${pageName}_DESCRIPTION`]?.value ||
            autoSeo.description ||
            seoFromConfig.description,
        additionalMetaTags: [
            {
                name: 'keywords',
                content:
                    config[`WEB_${pageKey}_KEYWORDS`]?.value ||
                    config[`WEB_${pageName}_KEYWORDS`]?.value ||
                    autoSeo.keywords ||
                    seoFromConfig.additionalMetaTags?.[0]?.content ||
                    '',
            },
        ],
    };

    // Update OpenGraph nếu có thay đổi
    if (finalSeo.openGraph) {
        finalSeo.openGraph.title = finalSeo.title;
        finalSeo.openGraph.description = finalSeo.description;
    }

    return finalSeo;
}
